import { VM_TRADING_API, getApiConfig } from '@/config/api';

export interface VMPortfolioData {
  account_status: string;
  buying_power: number;
  portfolio_value: number;
  cash: number;
  positions_count: number;
  day_trade_count: number;
  pattern_day_trader: boolean;
}

export interface VMEngineStatus {
  trading_engine_running: boolean;
  strategies_active: number;
  last_update: string;
  uptime_seconds: number;
  orders_today: number;
  positions_count: number;
  current_strategy?: string;
}

export interface VMBacktestRequest {
  strategy_id: string;
  strategy: any;
  symbols: any[];
  baskets?: any[];
  settings: any;
}

export interface VMOrderRequest {
  symbol: string;
  qty?: number;
  notional?: number;
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop';
  time_in_force: 'day' | 'gtc' | 'ioc' | 'fok';
  limit_price?: number;
  stop_price?: number;
  trail_percent?: number;
  order_class?: 'simple' | 'bracket' | 'oco' | 'oto';
  take_profit?: {
    limit_price: number;
  };
  stop_loss?: {
    stop_price: number;
    limit_price?: number;
  };
  trading_mode?: 'PAPER' | 'LIVE'; // CRITICAL: Add trading mode support
}

export interface VMWebSocketMessage {
  type: 'portfolio_update' | 'engine_status' | 'order_update' | 'error' | 'heartbeat';
  data: any;
  timestamp: string;
}

// Coderabbit suggestions: Add interfaces for API responses
interface FastAPIResponse<T> {
  success: boolean;
  data: {
    positions?: T[];
    orders?: T[];
    trading_mode: string;
  };
  timestamp: string;
}

interface LegacyResponse<T> {
  positions?: T[];
  orders?: T[];
  _metadata?: any;
}

class VMTradingService {
  private baseUrl: string;
  private wsUrl: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private isConnected = false;

  constructor() {
    const config = getApiConfig();
    this.baseUrl = config.TRADING_BASE_URL;
    this.wsUrl = config.TRADING_WS_URL;
  }

  /**
   * Coderabbit suggestion: Debug logging utility
   */
  private debugLog(message: string, ...data: any[]) {
    if (process.env.REACT_APP_DEBUG_VM_SERVICE === 'true') {
      console.log(`[VMTradingService] ${message}`, ...data);
    }
  }

  /**
   * Get authentication headers
   */
  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  /**
   * Make HTTP request to VM API with error handling and timeout
   */
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Add timeout for localhost development to prevent hanging
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    
    const timeoutMs = isLocalhost ? 5000 : 30000; // 5 seconds for localhost, 30 seconds for production
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      this.debugLog(`${options.method || 'GET'} ${url} (timeout: ${timeoutMs}ms)`);
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // Handle non-JSON responses
        const text = await response.text();
        console.warn(`Non-JSON response from ${url}:`, text);
        return text as any;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutError = new Error(`Request timeout after ${timeoutMs}ms for ${url}`);
        console.error(`VMTradingService timeout:`, timeoutError);
        throw timeoutError;
      }
      
      console.error(`VMTradingService request failed for ${url}:`, error);
      throw new Error(`VM API request failed: ${error.message}`);
    }
  }

  /**
   * Test connection to VM
   */
  async testConnection(): Promise<{ success: boolean; details: any }> {
    // Extract VM IP from base URL for logging
    const vmHost = this.baseUrl.replace(/^https?:\/\//, '').replace(/:\d+$/, '');
    
    try {
      const health = await this.getHealth();
      return {
        success: true,
        details: {
          message: 'Successfully connected to VM trading API',
          health,
          vm_host: vmHost,
          timestamp: new Date().toISOString()
        }
      };
    } catch (error) {
      return {
        success: false,
        details: {
          message: 'Failed to connect to VM trading API',
          error: error.message,
          vm_host: vmHost,
          timestamp: new Date().toISOString()
        }
      };
    }
  }

  /**
   * Get API health status
   */
  async getHealth(): Promise<any> {
    return this.makeRequest(VM_TRADING_API.ENDPOINTS.HEALTH);
  }

  /**
   * Get portfolio information with DYNAMIC TRADING MODE
   */
  async getPortfolio(trading_mode: string = 'PAPER'): Promise<VMPortfolioData> {
    const endpoint = `${VM_TRADING_API.ENDPOINTS.PORTFOLIO}?trading_mode=${trading_mode.toUpperCase()}`;
    
    try {
      const result = await this.makeRequest<any>(endpoint);
      
      console.log(`VMTradingService: ${trading_mode} portfolio raw response:`, result);
      
      // Handle different response formats
      if (result && typeof result === 'object') {
        // FastAPI format: { success: true, data: { cash: ..., portfolio_value: ... } }
        if ('success' in result && result.success && 'data' in result) {
          console.log(`VMTradingService: ${trading_mode} FastAPI portfolio format`);
          return result.data as VMPortfolioData;
        }
        // Direct format: { cash: ..., portfolio_value: ... }
        else if ('cash' in result || 'portfolio_value' in result) {
          console.log(`VMTradingService: ${trading_mode} direct portfolio format`);
          return result as VMPortfolioData;
        }
      }
      
      // If no valid format found, return empty portfolio
      console.warn(`VMTradingService: ${trading_mode} unexpected portfolio response format`);
      return {
        account_status: 'UNKNOWN',
        buying_power: 0,
        portfolio_value: 0,
        cash: 0,
        positions_count: 0,
        day_trade_count: 0,
        pattern_day_trader: false
      };
    } catch (error) {
      console.warn(`VMTradingService: Portfolio endpoint failed for ${trading_mode}:`, error);
      // Return fallback data instead of throwing
      return {
        account_status: 'UNAVAILABLE',
        buying_power: trading_mode === 'PAPER' ? 100000 : 0,
        portfolio_value: trading_mode === 'PAPER' ? 100000 : 0,
        cash: trading_mode === 'PAPER' ? 100000 : 0,
        positions_count: 0,
        day_trade_count: 0,
        pattern_day_trader: false
      };
    }
  }

  /**
   * Get trading engine status
   */
  async getEngineStatus(): Promise<VMEngineStatus> {
    return this.makeRequest<VMEngineStatus>(VM_TRADING_API.ENDPOINTS.ENGINE_STATUS);
  }

  /**
   * Start trading engine
   */
  async startTradingEngine(): Promise<any> {
    return this.makeRequest(VM_TRADING_API.ENDPOINTS.ENGINE_START, {
      method: 'POST',
    });
  }

  /**
   * Stop trading engine
   */
  async stopTradingEngine(): Promise<any> {
    return this.makeRequest(VM_TRADING_API.ENDPOINTS.ENGINE_STOP, {
      method: 'POST',
    });
  }

  /**
   * Run backtest on VM with fallback to direct endpoint
   */
  async runBacktest(request: VMBacktestRequest): Promise<any> {
    console.log('VMTradingService: Sending full ML strategy request to VM:', request);
    
    try {
      // First try the configured endpoint (usually /vm-api/api/backtest/run)
      return await this.makeRequest(VM_TRADING_API.ENDPOINTS.BACKTEST, {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      // If we get a 404, try the direct endpoint without /vm-api prefix
      if (error.message.includes('HTTP 404') || error.message.includes('Not Found')) {
        console.log('VMTradingService: Proxied endpoint not found, trying direct endpoint...');
        
        // Try direct endpoint by temporarily changing base URL
        const originalBaseUrl = this.baseUrl;
        this.baseUrl = 'http://localhost:8000'; // Direct to enhanced ML server
        
        try {
          const result = await this.makeRequest(VM_TRADING_API.ENDPOINTS.BACKTEST, {
            method: 'POST',
            body: JSON.stringify(request),
          });
          console.log('VMTradingService: Direct endpoint succeeded!');
          return result;
        } finally {
          // Always restore original base URL
          this.baseUrl = originalBaseUrl;
        }
      }
      
      // Re-throw original error if it's not a 404 or direct endpoint also failed
      throw error;
    }
  }

  /**
   * Place order through VM with DYNAMIC TRADING MODE - CRITICAL SECURITY FIX
   */
  async placeOrder(orderRequest: VMOrderRequest): Promise<any> {
    try {
      const trading_mode = orderRequest.trading_mode || 'PAPER';
      
      console.log(`VMTradingService: Placing ${trading_mode} order on VM`);
      console.log('VMTradingService: Order request:', orderRequest);
      
      // Prepare order data for VM API
      const vmOrderData = {
        symbol: orderRequest.symbol,
        qty: orderRequest.qty,
        side: orderRequest.side,
        type: orderRequest.type,
        time_in_force: orderRequest.time_in_force,
        trading_mode: trading_mode, // CRITICAL: Pass trading mode to VM
        limit_price: orderRequest.limit_price,
        stop_price: orderRequest.stop_price,
        client_order_id: `vm_order_${Date.now()}`
      };
      
      // Make real call to VM orders endpoint
      const result = await this.makeRequest('/api/orders', {
        method: 'POST',
        body: JSON.stringify(vmOrderData),
      });
      
      console.log(`VMTradingService: VM ${trading_mode} order placed successfully:`, result);
      return result;
      
    } catch (error) {
      console.error('VMTradingService: Order placement failed:', error);
      
      // Fallback to simulation only if VM is down
      console.warn('VMTradingService: Falling back to simulated order due to VM error');
      return {
        id: `vm_sim_${Date.now()}`,
        client_order_id: `vm_order_${Date.now()}`,
        symbol: orderRequest.symbol,
        qty: orderRequest.qty,
        side: orderRequest.side,
        type: orderRequest.type,
        time_in_force: orderRequest.time_in_force,
        trading_mode: orderRequest.trading_mode || 'PAPER',
        status: 'simulation_fallback',
        filled_qty: orderRequest.qty,
        filled_avg_price: 100.00,
        created_at: new Date().toISOString(),
        source: 'vm_simulation_fallback',
        error: error.message,
        note: 'Simulated order - VM endpoint failed'
      };
    }
  }

  /**
   * Get account information with DYNAMIC TRADING MODE (mapped to portfolio endpoint)
   */
  async getAccount(trading_mode: string = 'PAPER'): Promise<any> {
    try {
      const endpoint = `${VM_TRADING_API.ENDPOINTS.ACCOUNT}?trading_mode=${trading_mode.toUpperCase()}`;
      
      try {
        // First try the configured endpoint (usually /vm-api/api/portfolio)
        const portfolioData = await this.makeRequest(endpoint);
        
        // If portfolio is empty or doesn't have account info, return mock account data
        if (!portfolioData || Object.keys(portfolioData).length === 0) {
          console.log('VMTradingService: Portfolio endpoint returned empty data, using fallback account');
          return this.getFallbackAccountData();
        }
        
        // If we have portfolio data, transform it to account format
        const data = portfolioData as any; // Type assertion for API response
        return {
          account_number: data?.account_id || 'VM_ACCOUNT',
          id: data?.account_id || 'VM_ACCOUNT',
          cash: data?.cash || 0,
          portfolio_value: data?.portfolio_value || 0,
          buying_power: data?.buying_power || 0,
          status: data?.account_status || 'ACTIVE',
          trading_blocked: data?.trading_blocked || false,
          pattern_day_trader: data?.pattern_day_trader || false,
          currency: 'USD',
          created_at: data?.created_at || new Date().toISOString(),
          account_type: 'paper',
          source: 'vm_trading_server',
          raw_portfolio_data: portfolioData
        };
      } catch (proxyError) {
        // If we get a 503, 502, or 404, try the direct endpoint without /vm-api prefix
        if (proxyError.message.includes('HTTP 503') || 
            proxyError.message.includes('HTTP 502') || 
            proxyError.message.includes('HTTP 404') || 
            proxyError.message.includes('SERVICE UNAVAILABLE')) {
          
          // Don't log detailed errors for expected proxy failures
          console.log('VMTradingService: Proxy endpoint unavailable, trying direct connection...');
          
          // Try direct endpoint by temporarily changing base URL
          const originalBaseUrl = this.baseUrl;
          this.baseUrl = 'http://localhost:8000'; // Direct to enhanced ML server
          
          try {
            const portfolioData = await this.makeRequest(endpoint);
            console.log('VMTradingService: Direct account endpoint succeeded!');
            
            // Transform portfolio data to account format
            const data = portfolioData as any;
            return {
              account_number: data?.account_id || 'VM_ACCOUNT',
              id: data?.account_id || 'VM_ACCOUNT',
              cash: data?.cash || 0,
              portfolio_value: data?.portfolio_value || 0,
              buying_power: data?.buying_power || 0,
              status: data?.account_status || 'ACTIVE',
              trading_blocked: data?.trading_blocked || false,
              pattern_day_trader: data?.pattern_day_trader || false,
              currency: 'USD',
              created_at: data?.created_at || new Date().toISOString(),
              account_type: 'paper',
              source: 'vm_trading_server_direct',
              raw_portfolio_data: portfolioData
            };
          } catch (directError) {
            // Direct connection also failed, use fallback silently
            console.log('VMTradingService: Direct connection also failed, using fallback account data');
            return this.getFallbackAccountData();
          } finally {
            // Always restore original base URL
            this.baseUrl = originalBaseUrl;
          }
        }
        
        // If it's not a proxy issue, still use fallback instead of throwing
        console.log('VMTradingService: Account endpoint error, using fallback account data');
        return this.getFallbackAccountData();
      }
    } catch (error) {
      // Any other error - use fallback silently
      console.log('VMTradingService: Account service unavailable, using fallback account data');
      return this.getFallbackAccountData();
    }
  }

  /**
   * Get fallback account data when VM services are unavailable
   */
  private getFallbackAccountData() {
    return {
      account_number: 'DEMO_ACCOUNT',
      id: 'DEMO_ACCOUNT',
      cash: 100000.00,
      portfolio_value: 100000.00,
      buying_power: 100000.00,
      status: 'ACTIVE',
      trading_blocked: false,
      pattern_day_trader: false,
      currency: 'USD',
      created_at: new Date().toISOString(),
      account_type: 'paper',
      source: 'demo_fallback',
      note: 'Demo account data - VM services unavailable'
    };
  }

  /**
   * Get current positions with DYNAMIC TRADING MODE
   */
  async getPositions(trading_mode: string = 'PAPER'): Promise<any> {
    try {
      const endpoint = `/api/positions?trading_mode=${trading_mode.toUpperCase()}`;
      const result = await this.makeRequest(endpoint);
      
      // DETAILED DEBUGGING: Log the exact response
      console.log(`VMTradingService: Retrieved ${trading_mode} positions from VM`);
      console.log(`VMTradingService: ${trading_mode} positions raw response:`, result);
      console.log(`VMTradingService: ${trading_mode} positions response type:`, typeof result);
      console.log(`VMTradingService: ${trading_mode} positions response keys:`, result ? Object.keys(result) : 'null/undefined');
      
      // Handle different response formats
      if (result && typeof result === 'object') {
        // FastAPI format: { success: true, data: { positions: [...], trading_mode: "LIVE" } }
        if ('success' in result && result.success && 'data' in result) {
          const fastApiResult = result as { success: boolean, data: { positions: any[], trading_mode: string } };
          console.log(`VMTradingService: ${trading_mode} FastAPI positions format - ${fastApiResult.data.positions.length} positions from Alpaca`);
          if (fastApiResult.data.positions.length > 0) {
            console.log(`VMTradingService: ${trading_mode} first Alpaca position:`, fastApiResult.data.positions[0]);
          }
          return fastApiResult.data.positions; // Return just the positions array for compatibility
        }
        // Legacy format: { positions: [...] }
        else if ('positions' in result) {
          const resultWithPositions = result as { positions: any[] };
          console.log(`VMTradingService: ${trading_mode} legacy positions format - ${resultWithPositions.positions.length} positions`);
          if (resultWithPositions.positions.length > 0) {
            console.log(`VMTradingService: ${trading_mode} first legacy position:`, resultWithPositions.positions[0]);
          }
          return resultWithPositions.positions;
        }
      }
      
      // Direct array format
      if (Array.isArray(result)) {
        console.log(`VMTradingService: ${trading_mode} direct array positions - ${result.length} positions`);
        if (result.length > 0) {
          console.log(`VMTradingService: ${trading_mode} first position sample:`, result[0]);
        }
      return result;
      }
      
      console.warn(`VMTradingService: ${trading_mode} unexpected positions response format`);
      return [];
    } catch (error) {
      console.warn('VMTradingService: Positions endpoint failed, returning empty array:', error);
      return [];
    }
  }

  /**
   * Get orders with DYNAMIC TRADING MODE and optional status filtering
   */
  async getOrders(trading_mode: string = 'PAPER', status?: string): Promise<any> {
    try {
      let endpoint = `${VM_TRADING_API.ENDPOINTS.ORDERS}?trading_mode=${trading_mode.toUpperCase()}`;
      
      // Add status filter if provided
      if (status) {
        endpoint += `&status=${status}`;
      }
      
      const result: any = await this.makeRequest<LegacyResponse<any> | FastAPIResponse<any> | any[]>(endpoint);
      
      this.debugLog(`${trading_mode} raw response:`, result);

      if (result) {
        // Handle different response formats gracefully
        // New FastAPI format: { success: true, data: { orders: [...] } }
        if (result.success && result.data?.orders) {
          const fastApiResult = result as FastAPIResponse<any>;
          const mappedOrders = (fastApiResult.data.orders || []).map((alpacaOrder: any) => {
            // Return all available fields from the Alpaca API order object
            return alpacaOrder;
          });

          this.debugLog(`${trading_mode} FastAPI format - ${mappedOrders.length} orders from Alpaca`);
          if (mappedOrders.length > 0) {
            this.debugLog(`${trading_mode} first Alpaca order:`, mappedOrders[0]);
          }
          return mappedOrders;
        }
        
        // Legacy format from older VM versions or direct from Alpaca
        if (Array.isArray(result.orders)) {
          this.debugLog(`${trading_mode} Legacy format (with 'orders' key) - ${result.orders.length} orders`);
          return result.orders;
        }
        
        // Direct array response
        if (Array.isArray(result)) {
          this.debugLog(`${trading_mode} Legacy format (direct array) - ${result.length} orders`);
          return result;
        }

        // Handle simulation/fallback response with metadata
        if (result._metadata && result.orders) {
          this.debugLog(`${trading_mode} VM simulation/fallback response - ${result.orders.length} orders`);
      return result;
        }
      }

      this.debugLog(`No orders found or unknown format for ${trading_mode}.`);
      return []; // Return empty array if format is not recognized
    } catch (error) {
      console.error(`VMTradingService: Failed to get ${trading_mode} orders`, error);
      throw error;
    }
  }

  /**
   * WebSocket Connection Management
   */

  /**
   * Connect to WebSocket for real-time updates
   */
  connectWebSocket(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        console.log(`VMTradingService: Connecting to Socket.IO server at ${this.wsUrl}`);
        
        // Use Socket.IO instead of standard WebSocket
        const { vmSocketService } = await import('./vmSocketService');
        
        // Connect to Socket.IO server
        await vmSocketService.connect();
        
        console.log('VMTradingService: Socket.IO connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        
        // Set up event handlers
        vmSocketService.on('strategy_update', (data) => {
          console.log('VMTradingService: Strategy update received:', data);
          const handler = this.messageHandlers.get('strategy_update');
          if (handler) handler(data);
        });
        
        vmSocketService.on('strategy_started', (data) => {
          console.log('VMTradingService: Strategy started:', data);
          const handler = this.messageHandlers.get('strategy_started');
          if (handler) handler(data);
        });
        
        vmSocketService.on('strategy_stopped', (data) => {
          console.log('VMTradingService: Strategy stopped:', data);
          const handler = this.messageHandlers.get('strategy_stopped');
          if (handler) handler(data);
        });
        
        resolve();
        
      } catch (error) {
        console.error('VMTradingService: Socket.IO connection failed:', error);
        this.isConnected = false;
        reject(new Error(`Socket.IO connection failed: ${error.message}`));
      }
    });
  }

  /**
   * Attempt to reconnect WebSocket
   */
  private attemptReconnect() {
    this.reconnectAttempts++;
    console.log(`VMTradingService: Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connectWebSocket().catch((error) => {
        console.error('VMTradingService: Reconnection failed:', error);
        
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('VMTradingService: Max reconnection attempts reached');
        }
      });
    }, this.reconnectDelay);
    
    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }

  /**
   * Disconnect WebSocket
   */
  disconnectWebSocket() {
    console.log('VMTradingService: Manually disconnecting Socket.IO');
    import('./vmSocketService').then(({ vmSocketService }) => {
      vmSocketService.disconnect();
    });
    this.isConnected = false;
  }

  /**
   * Register message handler for specific message types
   */
  onWebSocketMessage(messageType: string, handler: (data: any) => void) {
    this.messageHandlers.set(messageType, handler);
  }

  /**
   * Remove message handler
   */
  offWebSocketMessage(messageType: string) {
    this.messageHandlers.delete(messageType);
  }

  /**
   * Send message through WebSocket
   */
  sendWebSocketMessage(message: any) {
    if (this.isConnected) {
      import('./vmSocketService').then(({ vmSocketService }) => {
        vmSocketService.emit('message', message);
      });
    } else {
      console.warn('VMTradingService: Cannot send message - Socket.IO not connected');
    }
  }

  /**
   * Get WebSocket connection status
   */
  getWebSocketStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      protocol: 'socket.io'
    };
  }

  /**
   * Strategy Management
   */

  /**
   * Deploy strategy to VM
   */
  async deployStrategy(strategyData: any): Promise<any> {
    return this.makeRequest('/api/strategies/deploy', {
      method: 'POST',
      body: JSON.stringify(strategyData),
    });
  }

  /**
   * Stop strategy on VM
   */
  async stopStrategy(strategyId: string): Promise<any> {
    return this.makeRequest(`/api/strategies/${strategyId}/stop`, {
      method: 'POST',
    });
  }

  /**
   * Get strategy status
   */
  async getStrategyStatus(strategyId: string): Promise<any> {
    return this.makeRequest(`/api/strategies/${strategyId}/status`);
  }

  /**
   * Discover available VM endpoints and provide implementation guidance
   */
  async discoverEndpoints(customBaseUrl?: string): Promise<{
    available: string[],
    missing: string[],
    recommendations: string[],
    vmUrl: string
  }> {
    const baseUrl = customBaseUrl || this.baseUrl;
    const endpoints = [
      '/api/health',
      '/api/portfolio',
      '/api/orders',
      '/api/positions',
      '/api/strategies',
      '/api/strategies/start',
      '/api/market-data'
    ];
    
    const available: string[] = [];
    const missing: string[] = [];
    const recommendations: string[] = [];
    
    console.log(`VMTradingService: Discovering endpoints at ${baseUrl}`);
    
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(`${baseUrl}${endpoint}`, {
          method: 'GET',
          headers: this.getHeaders(),
        });
        
        if (response.ok || response.status === 405) { // 405 = Method Not Allowed means endpoint exists
          available.push(endpoint);
        } else {
          missing.push(endpoint);
        }
      } catch (error) {
        missing.push(endpoint);
      }
    }
    
    // Generate recommendations for missing endpoints
    if (missing.includes('/api/orders')) {
      recommendations.push(`
VM Orders Endpoint Implementation:
Add this to your VM server:

@app.route('/api/orders', methods=['GET'])
def get_orders():
    # Return list of orders from your trading system
    # For paper trading, you can simulate orders
    return jsonify({
        "orders": [],
        "status": "success"
    })

@app.route('/api/orders', methods=['POST']) 
def place_order():
    order_data = request.json
    # Implement order placement logic here
    return jsonify({
        "order_id": f"vm_order_{int(time.time())}",
        "status": "submitted",
        "message": "Order placed successfully"
    })
      `);
    }
    
    if (missing.includes('/api/positions')) {
      recommendations.push(`
VM Positions Endpoint Implementation:
Add this to your VM server:

@app.route('/api/positions', methods=['GET'])
def get_positions():
    return jsonify({
        "positions": [],
        "total_value": 0,
        "status": "success"
    })
      `);
    }
    
    return { available, missing, recommendations, vmUrl: baseUrl };
  }
}

// Export singleton instance
export const vmTradingService = new VMTradingService();
export default vmTradingService; 